import axios from 'axios';
import tokenService from '../utils/token';
import apiConfig from '../utils/apiConfig';

const APP_ENV = process.env.REACT_APP_APP_ENV || 'local';

const API_ENV_URLS = {
  local: 'http://localhost:6010/api',
  stage: 'https://api.ratnakarsecurities.com/api',
  prod: 'https://api.ratnakarsecurities.com/api',
  production: 'https://api.ratnakarsecurities.com/api',
};

const resolvedBaseURL =
  process.env.REACT_APP_API_BASE_URL ||
  API_ENV_URLS[APP_ENV.toLowerCase()] ||
  API_ENV_URLS.local;

const apiClient = axios.create({
  baseURL: resolvedBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if it is a refresh request or a login request to prevent infinite loops
    if (
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        const refreshToken = tokenService.getRefreshToken();
        if (!refreshToken) {
          isRefreshing = false;
          window.dispatchEvent(new CustomEvent('auth:logout', { detail: 'Session expired' }));
          reject(error);
          return;
        }

        axios
          .post(`${apiClient.defaults.baseURL}${apiConfig.auth.refresh}`, { refreshToken })
          .then(({ data }) => {
            const { accessToken, refreshToken: newRefresh } = data;
            tokenService.setTokens(accessToken, newRefresh);

            apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

            processQueue(null, accessToken);
            resolve(apiClient(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            tokenService.clearTokens();
            window.dispatchEvent(new CustomEvent('auth:logout', { detail: 'Token invalid or expired. Please sign in again.' }));
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
